// Clio data sync — the admin surface for the mirrors.
//
// This page exists because I spent four sessions asking Paul to make
// raw API calls, and unsurprisingly they never got made. Sync and drift
// were backend-only, so the mirrors stayed empty, which in turn kept
// party classification and conflict checking pointless. A button would
// have unblocked all of it.
//
// The page is built around one idea: the drift report is the decision,
// not the sync. Anyone can press Sync. What matters is whether the
// mirror then AGREES with Clio, because until it does, nothing should
// read from it. So drift is the loud half of every card and the verdict
// is written in words rather than left as a count to interpret.
//
// Order on the page is the real dependency order — matters, then
// contacts, then parties, which need matters to exist first. Doing them
// out of order produces confusing half-results, so the page states the
// order rather than trusting people to know it.

import React, { useCallback, useEffect, useState } from "react";
import { C } from "../constants.js";
import { apiFetch } from "../api.js";
import { Spinner } from "../components/Spinner.jsx";

export function DataSyncPage() {
  const [busy, setBusy] = useState(null);
  const [results, setResults] = useState({});
  const [drift, setDrift] = useState({});
  const [index, setIndex] = useState(null);
  const [webhooks, setWebhooks] = useState(null);
  const [error, setError] = useState(null);

  const loadStatus = useCallback(async () => {
    try {
      setIndex(await apiFetch("/api/conflicts/index"));
    } catch (e) { /* non-fatal */ }
    try {
      setWebhooks(await apiFetch("/api/admin/webhooks/status"));
    } catch (e) { /* non-fatal */ }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const call = async (key, path, opts) => {
    setBusy(key);
    setError(null);
    try {
      const d = await apiFetch(path, opts);
      if (key.endsWith("-drift")) setDrift((p) => ({ ...p, [key]: d }));
      else setResults((p) => ({ ...p, [key]: d }));
      await loadStatus();
      return d;
    } catch (e) {
      setError(`${key}: ${e.message}`);
      return null;
    } finally {
      setBusy(null);
    }
  };

  const stat = (label, value, hint) =>
    React.createElement("div", { key: label, style: { minWidth: "92px" } },
      React.createElement("div", {
        style: { fontSize: "10px", color: C.textLight, fontWeight: 700,
                 textTransform: "uppercase", letterSpacing: "0.06em" },
      }, label),
      React.createElement("div", {
        style: { fontSize: "18px", fontWeight: 700, color: C.navy, marginTop: "2px" },
      }, value),
      hint && React.createElement("div", {
        style: { fontSize: "10px", color: C.textLight, marginTop: "1px" },
      }, hint));

  const driftBlock = (key) => {
    const d = drift[key];
    if (!d) return null;
    const clean = d.in_sync;
    return React.createElement("div", {
      style: {
        marginTop: "14px", padding: "13px 15px", background: C.warmWhite,
        borderRadius: "8px", fontSize: "13px", color: C.textDark,
        lineHeight: 1.55,
      },
    },
      React.createElement("strong", {
        style: { display: "block", marginBottom: "5px", color: C.navy },
      }, clean ? "Mirror agrees with Clio" : "Mirror does not agree with Clio"),
      d.verdict,
      !clean && React.createElement("div", { style: { marginTop: "9px" } },
        d.missing_locally?.length > 0 && React.createElement("div", null,
          `Missing locally: ${d.missing_locally.slice(0, 12).join(", ")}`
          + (d.missing_locally.length > 12 ? ` … +${d.missing_locally.length - 12}` : "")),
        d.missing_in_clio?.length > 0 && React.createElement("div", { style: { marginTop: "4px" } },
          `Not in Clio's list (usually closed): ${d.missing_in_clio.slice(0, 12).join(", ")}`
          + (d.missing_in_clio.length > 12 ? ` … +${d.missing_in_clio.length - 12}` : "")),
        d.mismatches?.length > 0 && React.createElement("div", { style: { marginTop: "8px" } },
          React.createElement("div", { style: { fontWeight: 600, marginBottom: "3px" } },
            `${d.mismatches.length} field mismatch(es):`),
          ...d.mismatches.slice(0, 10).map((m, i) =>
            React.createElement("div", {
              key: i,
              style: { fontSize: "12px", color: C.textMuted, fontFamily: "ui-monospace, monospace" },
            }, `${m.display_number || m.clio_matter_id || m.clio_contact_id} · `
             + `${m.field}: local "${m.local}" vs clio "${m.clio}"`)),
        ),
      ),
      clean && React.createElement("div", {
        style: { marginTop: "7px", fontSize: "12px", color: C.textMuted },
      }, "Reads can safely be moved onto the local table for this domain."),
    );
  };

  const card = (opts) => React.createElement("div", { style: cardStyle },
    React.createElement("div", {
      style: { display: "flex", alignItems: "baseline", gap: "10px", flexWrap: "wrap" },
    },
      React.createElement("h2", { style: h2Style }, opts.title),
      React.createElement("div", { style: { flex: 1 } }),
      React.createElement("span", {
        style: { fontSize: "11px", color: C.textLight },
      }, opts.step),
    ),
    React.createElement("p", {
      style: { fontSize: "13px", color: C.textMuted, margin: "6px 0 13px", lineHeight: 1.5 },
    }, opts.blurb),
    opts.body,
  );

  return React.createElement("div", { style: { padding: "32px 40px", maxWidth: "880px" } },
    React.createElement("h1", {
      style: { fontFamily: "'Playfair Display', serif", fontSize: "32px",
               color: C.navy, marginBottom: "6px" },
    }, "Clio data sync"),
    React.createElement("p", {
      style: { fontSize: "14px", color: C.textMuted, marginBottom: "22px", lineHeight: 1.55 },
    }, "Mirrors Clio's matters, contacts and parties into local tables. "
     + "Nothing reads from these yet — the drift check below is what "
     + "decides when they can. Run them in the order shown; parties need "
     + "matters to exist first."),

    error && React.createElement("div", {
      style: { fontSize: "13px", color: C.textDark, background: C.warmWhite,
               border: `1px solid ${C.warmGray}`, borderRadius: "8px",
               padding: "11px 14px", marginBottom: "16px" },
    }, error),

    // Where things stand overall.
    index && React.createElement("div", {
      style: { display: "flex", gap: "26px", flexWrap: "wrap", padding: "14px 18px",
               background: "#fff", border: `1px solid ${C.warmGray}`,
               borderRadius: "12px", marginBottom: "20px" },
    },
      stat("Parties", index.parties, "searchable for conflicts"),
      stat("Contacts", index.contacts, "in the local mirror"),
      index.is_empty && React.createElement("div", {
        style: { fontSize: "12px", color: C.textDark, alignSelf: "center",
                 maxWidth: "420px", lineHeight: 1.5 },
      }, "Nothing indexed. Conflict checks will search nothing and stay "
       + "open rather than reporting clean."),
    ),

    // ── 1. Matters ──────────────────────────────────────────────
    card({
      title: "Matters", step: "Step 1",
      blurb: "Pulls open matters from Clio into the local table. Safe to "
           + "re-run — matters that vanish from Clio's list are reported, "
           + "never deleted.",
      body: React.createElement("div", null,
        React.createElement("div", { style: btnRow },
          React.createElement("button", {
            onClick: () => call("matters", "/api/admin/matters/sync", { method: "POST" }),
            disabled: !!busy, style: primaryBtn,
          }, busy === "matters" ? "Syncing…" : "Sync matters"),
          React.createElement("button", {
            onClick: () => call("matters-drift", "/api/admin/matters/drift"),
            disabled: !!busy, style: quietBtn,
          }, busy === "matters-drift" ? "Checking…" : "Check drift"),
        ),
        results.matters && React.createElement("div", { style: statRow },
          stat("Created", results.matters.created),
          stat("Updated", results.matters.updated),
          stat("Unchanged", results.matters.unchanged),
          stat("In Clio", results.matters.clio_count),
        ),
        driftBlock("matters-drift"),
      ),
    }),

    // ── 2. Contacts ─────────────────────────────────────────────
    card({
      title: "Contacts", step: "Step 2",
      blurb: "Mirrors the contact book. Identifying fields only — the "
           + "sensitive custom fields stay in Clio and are fetched on "
           + "demand rather than copied into a second store.",
      body: React.createElement("div", null,
        React.createElement("div", { style: btnRow },
          React.createElement("button", {
            onClick: () => call("contacts", "/api/admin/contacts/sync", { method: "POST" }),
            disabled: !!busy, style: primaryBtn,
          }, busy === "contacts" ? "Syncing…" : "Sync contacts"),
          React.createElement("button", {
            onClick: () => call("contacts-drift", "/api/admin/contacts/drift"),
            disabled: !!busy, style: quietBtn,
          }, busy === "contacts-drift" ? "Checking…" : "Check drift"),
        ),
        results.contacts && React.createElement("div", { style: statRow },
          stat("Created", results.contacts.created),
          stat("Updated", results.contacts.updated),
          stat("Unchanged", results.contacts.unchanged),
          stat("In Clio", results.contacts.clio_count),
        ),
        driftBlock("contacts-drift"),
      ),
    }),

    // ── 3. Parties ──────────────────────────────────────────────
    card({
      title: "Matter parties", step: "Step 3 — needs steps 1 and 2",
      blurb: "Who is involved in each matter, and on which side. This is "
           + "what conflict checking searches. Each matter costs a Clio "
           + "call, so it runs in batches — press again until it says "
           + "there is nothing left.",
      body: React.createElement("div", null,
        React.createElement("div", { style: btnRow },
          React.createElement("button", {
            onClick: () => call("parties", "/api/admin/parties/sync?limit=25", { method: "POST" }),
            disabled: !!busy, style: primaryBtn,
          }, busy === "parties" ? "Syncing a batch…" : "Sync next 25 matters"),
        ),
        results.parties && React.createElement("div", null,
          React.createElement("div", { style: statRow },
            stat("Matters done", results.parties.processed),
            stat("Parties added", results.parties.created),
            stat("Kept", results.parties.preserved, "confirmed adversity"),
            stat("Remaining", results.parties.remaining),
          ),
          React.createElement("div", {
            style: { marginTop: "10px", fontSize: "13px", color: C.textDark },
          }, results.parties.done
              ? "Every matter has had its parties synced."
              : `${results.parties.remaining} matter(s) still to go — run it again.`),
          results.parties.failed > 0 && React.createElement("div", {
            style: { marginTop: "9px", padding: "11px 13px", background: C.warmWhite,
                     borderRadius: "8px", fontSize: "12px", color: C.textDark },
          },
            React.createElement("div", { style: { fontWeight: 600, marginBottom: "4px" } },
              `${results.parties.failed} matter(s) failed — the rest of the batch still ran.`),
            ...(results.parties.errors || []).map((er, i) =>
              React.createElement("div", {
                key: i,
                style: { fontFamily: "ui-monospace, monospace", color: C.textMuted },
              }, `${er.clio_matter_id}: ${er.error}`)),
          ),
        ),
      ),
    }),

    // ── Webhooks ────────────────────────────────────────────────
    card({
      title: "Clio webhooks", step: "After any Clio app change",
      blurb: "Webhook subscriptions belong to the Clio application that "
           + "created them, so replacing the app kills them silently — no "
           + "error, matter-change events just stop arriving. Re-register "
           + "after any credential change.",
      body: React.createElement("div", null,
        React.createElement("div", { style: btnRow },
          React.createElement("button", {
            onClick: () => call("webhooks", "/api/admin/webhooks/register", { method: "POST" }),
            disabled: !!busy, style: primaryBtn,
          }, busy === "webhooks" ? "Registering…" : "Re-register webhooks"),
          React.createElement("button", {
            onClick: loadStatus, disabled: !!busy, style: quietBtn,
          }, "Refresh status"),
        ),
        webhooks && React.createElement("div", {
          style: { marginTop: "12px", fontSize: "13px", color: C.textDark },
        },
          webhooks.configured === false
            ? "WEBHOOK_BASE_URL is not set, so Clio has nowhere to send events."
            : `${(webhooks.registrations || webhooks.data || []).length || 0} subscription(s) registered`
              + (webhooks.callback_url ? ` · ${webhooks.callback_url}` : "")),
      ),
    }),

    busy && React.createElement("div", {
      style: { display: "flex", gap: "8px", alignItems: "center",
               fontSize: "13px", color: C.textMuted, padding: "4px 2px" },
    }, React.createElement(Spinner, { size: 15 }),
       "Talking to Clio — this can take a moment."),
  );
}

const cardStyle = {
  background: "#fff", borderRadius: "12px", padding: "20px 24px",
  border: "1px solid #E8E3DC", marginBottom: "20px",
};
const h2Style = {
  fontSize: "13px", color: "#6B6660", textTransform: "uppercase",
  letterSpacing: "0.08em", fontWeight: 600, margin: 0,
};
const btnRow = { display: "flex", gap: "8px", flexWrap: "wrap" };
const statRow = {
  display: "flex", gap: "24px", flexWrap: "wrap", marginTop: "14px",
  padding: "12px 14px", background: "#F5F2ED", borderRadius: "8px",
};
const quietBtn = {
  border: "1px solid #E8E3DC", background: "#fff", borderRadius: "6px",
  padding: "8px 14px", fontSize: "12px", fontWeight: 600,
  color: "#1B2A4A", cursor: "pointer", flexShrink: 0,
};
const primaryBtn = {
  border: "1px solid #1B2A4A", background: "#1B2A4A", borderRadius: "6px",
  padding: "8px 16px", fontSize: "12px", fontWeight: 600,
  color: "#fff", cursor: "pointer", flexShrink: 0,
};
